import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { AlertTriangle, Building2, ChevronDown, ChevronRight, GitBranch, Loader2, Network } from 'lucide-react';
import { useState } from 'react';
import { MermaidInline } from '../../../tools/atoms/MermaidInline';
// Type guard for success output
export const isCorporateFamilyTreeOutput = (output)=>{
    return typeof output === 'object' && output !== null && 'targetCompany' in output && 'mermaidDiagram' in output && 'confidenceScore' in output && typeof output.targetCompany === 'object' && typeof output.mermaidDiagram === 'string';
};
// Type guard for error output
export const isCorporateFamilyTreeErrorOutput = (output)=>{
    return typeof output === 'object' && output !== null && 'message' in output && 'code' in output && typeof output.code === 'string';
};
// Helper to get confidence color class
const getConfidenceColor = (score)=>{
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
};
// Helper to extract company name from error args
const getCompanyNameFromError = (error)=>{
    try {
        const argsJson = error.details?.details?.argsJson;
        if (argsJson) {
            const args = JSON.parse(argsJson);
            return args.companyName || null;
        }
    } catch  {
    // Ignore parse errors
    }
    return null;
};
export function CorporateFamilyTreeToolCall({ output, isRunning }) {
    const [subsidiariesExpanded, setSubsidiariesExpanded] = useState(false);
    const [hierarchyExpanded, setHierarchyExpanded] = useState(false);
    const [warningsExpanded, setWarningsExpanded] = useState(false);
    // Loading state
    if (isRunning) {
        return /*#__PURE__*/ _jsx("div", {
            className: "border rounded-lg p-4 bg-muted/30",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Loader2, {
                        className: "h-4 w-4 animate-spin text-muted-foreground"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-medium",
                                children: "Analyzing corporate family tree..."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-muted-foreground mt-1",
                                children: "Discovering parent companies and subsidiaries"
                            })
                        ]
                    })
                ]
            })
        });
    }
    // No output state
    if (!output) {
        return null;
    }
    // Error state
    if (isCorporateFamilyTreeErrorOutput(output)) {
        const companyName = getCompanyNameFromError(output);
        return /*#__PURE__*/ _jsxs("div", {
            className: "border border-destructive/50 rounded-lg p-4 bg-destructive/10",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2 mb-2",
                    children: [
                        /*#__PURE__*/ _jsx(AlertTriangle, {
                            "data-testid": "error-icon",
                            className: "h-4 w-4 text-destructive"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-sm font-medium text-destructive",
                            children: "Corporate Family Tree Failed"
                        })
                    ]
                }),
                companyName && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm text-muted-foreground mb-2",
                    children: [
                        "Company: ",
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: companyName
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "text-sm text-destructive/90",
                    children: output.message
                }),
                output.details?.details?.errorMessage && /*#__PURE__*/ _jsx("div", {
                    className: "text-xs text-muted-foreground mt-2",
                    children: output.details.details.errorMessage
                })
            ]
        });
    }
    // Success state - need to cast since we checked for error above
    const successOutput = output;
    return /*#__PURE__*/ _jsxs("div", {
        className: "border rounded-lg bg-muted/30 overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between p-4 border-b",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Network, {
                                className: "h-4 w-4 text-muted-foreground",
                                "aria-hidden": "true"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm font-medium",
                                children: "Corporate Family Tree"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("output", {
                        className: "flex items-center gap-2",
                        "aria-label": `Confidence score: ${successOutput.confidenceScore} percent`,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                "data-testid": "confidence-indicator",
                                className: `w-2 h-2 rounded-full ${getConfidenceColor(successOutput.confidenceScore)}`,
                                "aria-hidden": "true"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                "aria-hidden": "true",
                                children: [
                                    successOutput.confidenceScore,
                                    "%"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-4 bg-background",
                children: /*#__PURE__*/ _jsx(MermaidInline, {
                    code: successOutput.mermaidDiagram
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "p-4 space-y-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 mb-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Building2, {
                                        className: "h-4 w-4 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-sm font-medium",
                                        children: successOutput.targetCompany.name
                                    })
                                ]
                            }),
                            successOutput.targetCompany.ueis.length > 0 && /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-wrap gap-1 ml-6",
                                children: successOutput.targetCompany.ueis.map((uei)=>/*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs font-mono",
                                        children: uei
                                    }, uei))
                            })
                        ]
                    }),
                    successOutput.parentCompany && /*#__PURE__*/ _jsxs("div", {
                        className: "border-l-2 border-muted pl-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-muted-foreground mb-1",
                                children: "Parent Company"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 flex-wrap",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-sm font-medium",
                                        children: successOutput.parentCompany.name
                                    }),
                                    /*#__PURE__*/ _jsx(Badge, {
                                        variant: "secondary",
                                        className: "text-xs",
                                        children: successOutput.parentCompany.parentType
                                    }),
                                    successOutput.parentCompany.publiclyTraded && /*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs",
                                        children: "Publicly Traded"
                                    })
                                ]
                            })
                        ]
                    }),
                    successOutput.parentHierarchy && successOutput.parentHierarchy.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setHierarchyExpanded(!hierarchyExpanded),
                                className: "flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground",
                                children: [
                                    hierarchyExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "h-3 w-3"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "h-3 w-3"
                                    }),
                                    "View full hierarchy (",
                                    successOutput.parentHierarchy.length,
                                    " levels)"
                                ]
                            }),
                            hierarchyExpanded && /*#__PURE__*/ _jsx("div", {
                                className: "mt-2 space-y-2 ml-4",
                                children: successOutput.parentHierarchy.map((parent, idx)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsx(GitBranch, {
                                                className: "h-3 w-3 text-muted-foreground"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: parent.name
                                            }),
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                className: "text-xs",
                                                children: parent.level
                                            })
                                        ]
                                    }, idx))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setSubsidiariesExpanded(!subsidiariesExpanded),
                                className: "flex items-center gap-1 text-sm",
                                "aria-label": "Toggle subsidiaries",
                                children: [
                                    subsidiariesExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "h-4 w-4"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "h-4 w-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: successOutput.subsidiaries.length > 0 ? `${successOutput.subsidiaries.length} Subsidiaries` : 'No subsidiaries found'
                                    })
                                ]
                            }),
                            subsidiariesExpanded && successOutput.subsidiaries.length > 0 && /*#__PURE__*/ _jsx("div", {
                                className: "mt-2 space-y-2 ml-6",
                                children: successOutput.subsidiaries.map((sub, idx)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 text-sm flex-wrap",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: sub.name
                                            }),
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                className: "text-xs",
                                                children: sub.relationshipType
                                            }),
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: sub.status === 'active' ? 'default' : 'secondary',
                                                className: "text-xs",
                                                children: sub.status
                                            })
                                        ]
                                    }, idx))
                            })
                        ]
                    }),
                    successOutput.dbas.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-muted-foreground mb-1",
                                children: "Also Known As"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-wrap gap-1",
                                children: successOutput.dbas.map((dba, idx)=>/*#__PURE__*/ _jsx(Badge, {
                                        variant: "secondary",
                                        className: "text-xs",
                                        children: dba.name
                                    }, idx))
                            })
                        ]
                    }),
                    successOutput.maStrategy && /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "M&A Strategy:"
                            }),
                            " ",
                            successOutput.maStrategy
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 text-xs text-muted-foreground flex-wrap",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Sources:"
                            }),
                            successOutput.dataSources.map((source, idx)=>/*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    className: "text-xs",
                                    children: source
                                }, idx))
                        ]
                    }),
                    successOutput.warnings && successOutput.warnings.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "warnings-section",
                        className: "border-t pt-3 mt-3",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setWarningsExpanded(!warningsExpanded),
                                className: "flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-500",
                                children: [
                                    /*#__PURE__*/ _jsx(AlertTriangle, {
                                        className: "h-3 w-3"
                                    }),
                                    warningsExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "h-3 w-3"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "h-3 w-3"
                                    }),
                                    successOutput.warnings.length,
                                    " warning",
                                    successOutput.warnings.length > 1 ? 's' : ''
                                ]
                            }),
                            warningsExpanded && /*#__PURE__*/ _jsx("ul", {
                                className: "mt-2 space-y-1 ml-4 text-xs text-muted-foreground",
                                children: successOutput.warnings.map((warning, idx)=>/*#__PURE__*/ _jsx("li", {
                                        children: warning
                                    }, idx))
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
